'use client';

import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AuthFormWrapper } from '@/app/(founder)/auth/_components/auth-form-wrapper';
import { ROUTES } from '@/constants/routes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { signup } from '@/app/api/auth/actions/actions';
import { RegisterFormValues, RegisterSchema } from '@/app/(founder)/auth/types';
import { getAuthError } from '@/utils/get-auth-error';
import { useAppContext } from '@/context/app-context';

export function RegisterForm() {
    const searchParams = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const callbackUrl = searchParams.get('callbackUrl') || ROUTES.Welcome;
    const email = searchParams.get('email') || '';
    const priceId = searchParams.get('priceId');
    const coupon = searchParams.get('coupon');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { goToPage } = useAppContext();

    const form = useForm<RegisterFormValues>({
        resolver: zodResolver(RegisterSchema),
        defaultValues: {
            email,
            password: '',
            confirmPassword: '',
        },
    });

    const onSubmit = async ({ email, password }: RegisterFormValues) => {
        setLoading(true);
        try {
            setError(null);

            const { error } = await signup({
                email,
                password,
            });

            if (error) {
                setError(getAuthError(error));
            } else {
                // Redirect to verify email page
                form.reset();
                const params = new URLSearchParams({
                    callbackUrl,
                    email,
                });

                // If priceId is present, construct checkout redirect URL
                if (priceId) {
                    const checkoutParams = new URLSearchParams({ priceId });
                    if (coupon) {
                        checkoutParams.append('coupon', coupon);
                    }
                    params.set(
                        'callbackUrl',
                        `${ROUTES.CheckoutRedirect}?${checkoutParams.toString()}`,
                    );
                }

                goToPage(`${ROUTES.VerifyEmail}?${params.toString()}`);
            }
        } catch (err: unknown) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const signIn = () => {
        const params = new URLSearchParams({
            callbackUrl,
            email,
        });

        // Pass priceId and coupon to login form
        if (priceId) {
            params.append('priceId', priceId);
        }
        if (coupon) {
            params.append('coupon', coupon);
        }

        goToPage(`${ROUTES.Login}?${params.toString()}`);
    };

    return (
        <AuthFormWrapper
            footer={
                <div className="text-muted-foreground text-xs text-center">
                    Already have an account?
                    <Button variant="link" size="xs" onClick={signIn}>
                        Log in
                    </Button>
                    <div className="text-center justify-start mt-4">
                        <span className="text-xs">
                            By clicking “Create Account” or “Continue with
                            Google” or “Continue with Apple”, you agree to the
                        </span>
                        <Button variant="link" size="xs" asChild>
                            <a
                                href="https://www.hyperfund.ai/terms.html"
                                target="blank"
                            >
                                HyperFund TOS
                            </a>
                        </Button>
                        <span className="text-xs">and acknowledge the</span>
                        <Button variant="link" size="xs" asChild>
                            <a
                                href="https://www.hyperfund.ai/privacy.html"
                                target="blank"
                            >
                                Privacy Policy
                            </a>
                        </Button>
                        <span className="text-xs">.</span>
                    </div>
                </div>
            }
        >
            <>
                {error && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="email"
                                            placeholder="Enter email"
                                            disabled={loading}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                {...field}
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Enter password"
                                                disabled={loading}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword,
                                                    )
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOff size={16} />
                                                ) : (
                                                    <Eye size={16} />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                {...field}
                                                type={
                                                    showConfirmPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Confirm password"
                                                disabled={loading}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3"
                                                onClick={() =>
                                                    setShowConfirmPassword(
                                                        !showConfirmPassword,
                                                    )
                                                }
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff size={16} />
                                                ) : (
                                                    <Eye size={16} />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={loading}
                            loading={loading}
                        >
                            Create Account
                        </Button>
                    </form>
                </Form>
            </>
        </AuthFormWrapper>
    );
}
